/**
 * Remembers where a signed-out visitor was headed, so that after signing in he
 * lands back there instead of on the default surface.
 *
 * Why this exists: the public doorway ends with "your 5 emails are ready — sign
 * up free to send" and sends him to /email. React Admin bounces an
 * unauthenticated visit to /login and hands the login page no destination, so
 * without this the promise would deliver him to the default surface and the
 * prepared emails he was just shown would look lost.
 *
 * Two rules keep this safe:
 *  - same-site paths only, so a stored value can never become an open redirect;
 *  - never an auth page, because bouncing back to /login would loop.
 */

const STORAGE_KEY = "raisy_post_login_path";

const AUTH_PATHS = [
  "/login",
  "/signup",
  "/register",
  "/forgot-password",
  "/request-access",
  "/confirmation-required",
];

/** A stored target is only honoured as a plain same-site path. */
function isSafePath(target: string | null): target is string {
  return !!target && target.startsWith("/") && !target.startsWith("//");
}

/**
 * The site root is not a destination, and storing it DESTROYS one (PO 2026-09-23).
 *
 * Measured on dev: the landing page's "your five emails are waiting" CTA opens `/messages`
 * with a client-side `<Link>`. The auth guard fires during that navigation — before the URL
 * commits — so it read `window.location.pathname` as `/`, wrote `/`, and the real destination
 * was gone. Signing in then landed on the default surface and the five drafts the visitor had
 * just been shown looked lost. (A hard navigation to `/messages`, where the URL is already
 * `/messages` when the guard runs, always stored correctly — which is why this read as working.)
 *
 * The guard cannot tell "he is on the root because he chose it" from "he is still on the root
 * because the navigation has not committed", so it must not guess. Refusing `/` costs nothing —
 * a signed-in visitor who asks for `/` is sent to the app's default surface anyway — and it
 * leaves whatever a real caller remembered intact.
 */
function isMeaningfulPath(target: string): boolean {
  return target !== "/";
}

function isAuthPath(target: string): boolean {
  return AUTH_PATHS.some(
    (p) => target === p || target.startsWith(`${p}?`) || target.startsWith(`${p}/`),
  );
}

/**
 * Store the path the visitor was trying to open. Call this from the auth guard,
 * at the moment it rejects an unauthenticated request.
 */
export function rememberPostLoginPath(path?: string): void {
  const target = (
    path ?? `${window.location.pathname}${window.location.search}`
  ).trim();
  if (!isSafePath(target) || !isMeaningfulPath(target) || isAuthPath(target)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, target);
  } catch {
    /* private mode — the visitor simply lands on the default surface */
  }
}

/**
 * Read and clear the stored path. One-shot by design: a destination remembered
 * for one sign-in must not redirect a later, unrelated sign-in.
 */
export function consumePostLoginPath(): string | null {
  try {
    const target = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return isSafePath(target) ? target : null;
  } catch {
    return null;
  }
}
